import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Skeleton } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import TenantWizard, { type TenantWizardData } from '@/components/TenantWizard';
import TenantSystemEmails from '@/components/TenantSystemEmails';
import { 
  ArrowLeft, 
  Building,
  Users, 
  Phone, 
  PhoneCall,
  Ban,
  CheckCircle,
  Edit,
  Trash2,
  Activity,
  Calendar,
  Key,
  ShieldOff,
  RotateCcw,
  Store,
  BarChart3,
  X,
  Package,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search,
  AlertCircle,
  Eye,
  Save,
  Mail,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { ChannelAnalyticsModal } from '@/components/ChannelAnalyticsModal';
import { TenantLimitsModal } from '@/components/TenantLimitsModal';
import { MODULE_CATEGORIES, getChildModules, toErr, type TenantProduct } from '@easyflow/shared';

interface VisibilityCatalogItem {
  id: string;
  code: string;
  display_name: string;
  logo_url: string | null;
  is_active: boolean;
  visible: boolean;
}

interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  status: string;
  domain?: string;
  sip_domain?: string;
  created_at: string;
  updated_at?: string;
  user_count: number;
  extension_count: number;
  trunk_count: number;
  store_count: number;
  call_count_24h: number;
  call_count_7d: number;
  total_call_duration: number;
  avg_call_duration: number;
  answered_calls: number;
  inbound_calls: number;
  outbound_calls: number;
  users?: TenantUser[];
  extensions?: TenantExtension[];
  trunks?: TenantTrunk[];
}

interface TenantStore {
  id: string;
  name: string;
  store_id: string;
  status: string;
  created_at: string;
}

interface TenantUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  created_at: string;
  totp_enabled?: boolean;
}

interface TenantExtension {
  id: string;
  extension_number: string;
  display_name: string;
  status: string;
}

interface TenantModule {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  enabled_at: string | null;
  disabled_at: string | null;
  category?: string;
  order?: number;
}

interface TenantTrunk {
  id: string;
  name: string;
  host: string;
  port: number;
  status: string;
}

interface TenantCompanyRaw {
  legal_name?: string;
  legal_form?: string;
  vat_number?: string;
  tax_code?: string;
  fiscal_code?: string;
  pec?: string;
  sdi_code?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  province?: string;
  country?: string;
  description?: string;
}

interface TenantDetailsRaw {
  tenant?: {
    id: string;
    name: string;
    slug: string;
    status: string;
    domain?: string;
    sip_domain?: string;
    created_at: string;
    updated_at?: string;
    language?: string;
    timezone?: string;
    product?: TenantProduct;
    companies?: TenantCompanyRaw[];
  };
  cdr_stats?: {
    calls_24h?: number | string;
    calls_7d?: number | string;
    total_duration_seconds?: number | string;
    avg_duration_seconds?: number | string;
    answered_calls?: number | string;
    inbound_calls?: number | string;
    outbound_calls?: number | string;
  };
  users?: { count?: number | string; items?: TenantUser[] };
  extensions?: { count?: number | string; items?: { id: string; extension: string; name?: string; status: string }[] };
  sip_trunks?: { count?: number | string; items?: { id: string; name: string; host: string; port?: number; status: string }[] };
  stores?: { count?: number | string; items?: { id: string; name: string; store_id?: string; status: string; created_at: string }[] };
}

function respErrMsg(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  if (typeof error === 'string' && error) return error;
  return fallback;
}

interface SupervisorComplianceData {
  supervisor_listen_tone_enabled: boolean;
  supervisor_listen_tone_url: string | null;
  supervisor_show_badge_to_agent: boolean;
}

function SupervisorComplianceSection({ tenantId }: { tenantId: string | undefined }) {
  const [data, setData] = useState<SupervisorComplianceData>({
    supervisor_listen_tone_enabled: false,
    supervisor_listen_tone_url: 'tone_stream://%(200,0,800)',
    supervisor_show_badge_to_agent: true,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const resp = await apiClient.get<{ data: SupervisorComplianceData } | SupervisorComplianceData>(`/master/tenants/${tenantId}/supervisor-compliance`);
        if (!alive) return;
        const raw = resp.data ?? (resp as unknown as SupervisorComplianceData);
        const next: SupervisorComplianceData = (raw && typeof raw === 'object' && 'data' in raw)
          ? (raw as { data: SupervisorComplianceData }).data
          : (raw as SupervisorComplianceData);
        setData({
          supervisor_listen_tone_enabled: next.supervisor_listen_tone_enabled ?? false,
          supervisor_listen_tone_url: next.supervisor_listen_tone_url ?? 'tone_stream://%(200,0,800)',
          supervisor_show_badge_to_agent: next.supervisor_show_badge_to_agent ?? true,
        });
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Errore di caricamento');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tenantId]);

  const save = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`/master/tenants/${tenantId}/supervisor-compliance`, data);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore di salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Caricamento…</div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Supervisor Compliance (Task #1336)</h3>
        <p className="text-xs text-muted-foreground">
          Configurazione tono iniziale per la modalità Listen e visibilità del badge "supervisione attiva" sul softphone agente.
          Default conservativo: tono OFF, badge ON.
        </p>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg border border-border dark:border-white/10">
        <div>
          <Label className="text-sm font-medium">Tono iniziale Listen</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Riproduce un beep al supervisor prima dell'eavesdrop (compliance audit-trail). Non udibile da agente/cliente.
          </p>
        </div>
        <Switch
          checked={data.supervisor_listen_tone_enabled}
          onCheckedChange={(v: boolean) => setData((d) => ({ ...d, supervisor_listen_tone_enabled: v }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supervisor-tone-url" className="text-sm font-medium">URL/path tono</Label>
        <Input
          id="supervisor-tone-url"
          value={data.supervisor_listen_tone_url ?? ''}
          onChange={(e) => setData((d) => ({ ...d, supervisor_listen_tone_url: e.target.value }))}
          placeholder="tone_stream://%(200,0,800)"
          disabled={!data.supervisor_listen_tone_enabled}
        />
        <p className="text-xs text-muted-foreground">
          Default: <code>tone_stream://%(200,0,800)</code>. Accetta qualsiasi sintassi FreeSWITCH playback (es. <code>/path/to/file.wav</code>).
        </p>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg border border-border dark:border-white/10">
        <div>
          <Label className="text-sm font-medium">Badge "supervisione attiva" su softphone agente</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Quando attivo, l'agente vede un indicatore ambra durante Listen/Whisper/Barge.
          </p>
        </div>
        <Switch
          checked={data.supervisor_show_badge_to_agent}
          onCheckedChange={(v: boolean) => setData((d) => ({ ...d, supervisor_show_badge_to_agent: v }))}
        />
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {savedAt && !error && <div className="text-sm text-emerald-600">Salvato.</div>}

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !tenantId}>
          {saving ? 'Salvataggio…' : 'Salva'}
        </Button>
      </div>

      <div className="text-xs text-muted-foreground/70 border-t border-border dark:border-white/10 pt-3">
        Modifica al dialplan: dopo il save, rigenera il dialplan FreeSWITCH del tenant
        (Tenant → Dialplan → Rigenera) perché il tono è iniettato nell'XML <code>supervisor_listen</code>.
      </div>
    </div>
  );
}

export default function TenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [extensions, setExtensions] = useState<TenantExtension[]>([]);
  const [trunks, setTrunks] = useState<TenantTrunk[]>([]);
  const [stores, setStores] = useState<TenantStore[]>([]);
  const [resettingPassword, setResettingPassword] = useState<string | null>(null);
  const [resetting2FA, setResetting2FA] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showChannelAnalytics, setShowChannelAnalytics] = useState(false);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showEditWizard, setShowEditWizard] = useState(false);
  const [editWizardData, setEditWizardData] = useState<TenantWizardData | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [tenantRawData, setTenantRawData] = useState<TenantDetailsRaw | null>(null);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [modules, setModules] = useState<TenantModule[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [togglingModule, setTogglingModule] = useState<string | null>(null);
  const [moduleSearch, setModuleSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});
  const [visibilityBrands, setVisibilityBrands] = useState<VisibilityCatalogItem[]>([]);
  const [visibilityAreas, setVisibilityAreas] = useState<VisibilityCatalogItem[]>([]);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);
  const [savingBrands, setSavingBrands] = useState(false);
  const [savingAreas, setSavingAreas] = useState(false);

  useEffect(() => {
    if (tenantId) {
      setVisibilityLoaded(false);
      setVisibilityBrands([]);
      setVisibilityAreas([]);
      setVisibilityLoading(false);
      setSavingBrands(false);
      setSavingAreas(false);
      void loadTenantDetail();
      void loadModules();
    }
  }, [tenantId]);

  const loadTenantDetail = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<TenantDetailsRaw>(`/master/dashboard/tenants/${tenantId}/details`);
      
      if (response.success && response.data) {
        const data = response.data;
        const tenantInfo = data.tenant;
        if (!tenantInfo) {
          setError('Failed to load tenant details');
          return;
        }
        const cdrStats = data.cdr_stats || {};
        
        setTenantRawData(data);
        setTenant({
          id: tenantInfo.id,
          name: tenantInfo.name,
          slug: tenantInfo.slug,
          status: tenantInfo.status,
          domain: tenantInfo.domain,
          sip_domain: tenantInfo.sip_domain,
          created_at: tenantInfo.created_at,
          updated_at: tenantInfo.updated_at,
          user_count: Number(data.users?.count) || 0,
          extension_count: Number(data.extensions?.count) || 0,
          trunk_count: Number(data.sip_trunks?.count) || 0,
          store_count: Number(data.stores?.count) || 0,
          call_count_24h: Number(cdrStats.calls_24h) || 0,
          call_count_7d: Number(cdrStats.calls_7d) || 0,
          total_call_duration: Number(cdrStats.total_duration_seconds) || 0,
          avg_call_duration: Number(cdrStats.avg_duration_seconds) || 0,
          answered_calls: Number(cdrStats.answered_calls) || 0,
          inbound_calls: Number(cdrStats.inbound_calls) || 0,
          outbound_calls: Number(cdrStats.outbound_calls) || 0,
        });
        
        if (data.users?.items) {
          setUsers(data.users.items);
        }
        if (data.extensions?.items) {
          setExtensions(data.extensions.items.map((ext) => ({
            id: ext.id,
            extension_number: ext.extension,
            display_name: ext.name || `${ext.extension}`,
            status: ext.status
          })));
        }
        if (data.sip_trunks?.items) {
          setTrunks(data.sip_trunks.items.map((trunk) => ({
            id: trunk.id,
            name: trunk.name,
            host: trunk.host,
            port: trunk.port || 5060,
            status: trunk.status
          })));
        }
        if (data.stores?.items) {
          setStores(data.stores.items.map((store) => ({
            id: store.id,
            name: store.name,
            store_id: store.store_id || '',
            status: store.status,
            created_at: store.created_at
          })));
        }
      } else {
        setError(respErrMsg(response.error, 'Failed to load tenant details'));
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      setError(__err.message || 'Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const loadVisibility = async () => {
    if (!tenantId) return;
    try {
      setVisibilityLoading(true);
      const [brandsResp, areasResp] = await Promise.all([
        apiClient.get<{ data?: VisibilityCatalogItem[] }>(`/master/tenants/${tenantId}/visibility/brands`),
        apiClient.get<{ data?: VisibilityCatalogItem[] }>(`/master/tenants/${tenantId}/visibility/areas`),
      ]);
      const brands = (brandsResp as { data?: VisibilityCatalogItem[] }).data ?? [];
      const areas = (areasResp as { data?: VisibilityCatalogItem[] }).data ?? [];
      setVisibilityBrands(brands);
      setVisibilityAreas(areas);
      setVisibilityLoaded(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore nel caricamento visibilità';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setVisibilityLoading(false);
    }
  };

  const toggleBrandVisibility = (brandId: string) => {
    setVisibilityBrands(prev => prev.map(b => b.id === brandId ? { ...b, visible: !b.visible } : b));
  };

  const toggleAreaVisibility = (areaId: string) => {
    setVisibilityAreas(prev => prev.map(a => a.id === areaId ? { ...a, visible: !a.visible } : a));
  };

  const saveBrandsVisibility = async () => {
    if (!tenantId) return;
    try {
      setSavingBrands(true);
      const hidden_ids = visibilityBrands.filter(b => !b.visible).map(b => b.id);
      await apiClient.post(`/master/tenants/${tenantId}/visibility/brands`, { hidden_ids });
      toast({ title: 'Salvato', description: 'Visibilità brand aggiornata' });
      await loadVisibility();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore salvataggio brand';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSavingBrands(false);
    }
  };

  const saveAreasVisibility = async () => {
    if (!tenantId) return;
    try {
      setSavingAreas(true);
      const hidden_ids = visibilityAreas.filter(a => !a.visible).map(a => a.id);
      await apiClient.post(`/master/tenants/${tenantId}/visibility/areas`, { hidden_ids });
      toast({ title: 'Salvato', description: 'Visibilità aree commerciali aggiornata' });
      await loadVisibility();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore salvataggio aree';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSavingAreas(false);
    }
  };

  const loadModules = async () => {
    if (!tenantId) return;
    try {
      setModulesLoading(true);
      const response = await apiClient.get<TenantModule[]>(`/master/tenants/${tenantId}/modules`);
      if (response.success && response.data) {
        setModules(response.data);
      }
    } catch (err: unknown) {
      console.error('Failed to load modules:', err);
    } finally {
      setModulesLoading(false);
    }
  };

  const toggleModule = async (moduleKey: string, enabled: boolean) => {
    if (!tenantId) return;
    try {
      setTogglingModule(moduleKey);
      await apiClient.put(`/master/tenants/${tenantId}/modules/${moduleKey}`, { enabled });
      setModules(prev => prev.map(m => 
        m.key === moduleKey 
          ? { ...m, enabled, enabled_at: enabled ? new Date().toISOString() : m.enabled_at, disabled_at: enabled ? null : new Date().toISOString() }
          : m
      ));
      toast({
        title: enabled ? 'Modulo attivato' : 'Modulo disattivato',
        description: `${modules.find(m => m.key === moduleKey)?.name || moduleKey}`,
      });
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore', description: __err.message || 'Impossibile aggiornare il modulo', variant: 'destructive' });
    } finally {
      setTogglingModule(null);
    }
  };

  const handleEditTenant = () => {
    if (!tenant || !tenantRawData) return;

    const tenantInfo = tenantRawData.tenant;
    if (!tenantInfo) return;
    // Task #1930 — il wizard edit NON modifica più le ragioni sociali.
    // Il CRUD multi-RS è gestito esclusivamente dal tab "Ragioni sociali"
    // della scheda tenant. Lo step "company" del wizard resta visibile in
    // sola lettura (precompilato dalla primaria) per dare contesto, ma
    // `handleUpdateTenant` ignora questi campi nel payload PUT.
    toast({
      title: 'Le ragioni sociali si gestiscono nel tab dedicato',
      description: 'Il wizard aggiorna solo i dati base del tenant. Per aggiungere/modificare/eliminare ragioni sociali usa il tab "Ragioni sociali".',
    });

    const wizardData = {
      basic: {
        name: tenant.name || '',
        slug: tenant.slug || '',
        sip_domain: tenant.sip_domain || '',
        status: tenant.status as 'active' | 'pending' | 'suspended' || 'active',
        language: tenantInfo.language || 'it',
        timezone: tenantInfo.timezone || 'Europe/Rome',
        product: tenantInfo.product ?? 'easyflow',
      },
      // Lasciato vuoto: ignorato dal payload PUT in `handleUpdateTenant`.
      company: {
        legal_name: '', legal_form: '', vat_number: '', fiscal_code: '',
        address: '', city: '', postal_code: '', province: '',
        country: 'Italia', pec: '', phone: '', description: '',
      },
      admin: {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        role: 'admin' as const,
      }
    };

    setEditWizardData(wizardData);
    setShowEditWizard(true);
  };

  const handleUpdateTenant = async (data: TenantWizardData) => {
    if (!tenantId) return;
    
    try {
      setIsUpdating(true);
      
      // Task #1930 — il payload PUT del wizard NON include più le ragioni
      // sociali: il CRUD multi-RS è gestito da `/api/tenant/companies`
      // (tab "Ragioni sociali"). Qui aggiorniamo SOLO i dati base del tenant.
      const updatePayload = {
        name: data.basic.name,
        slug: data.basic.slug,
        sip_domain: data.basic.sip_domain,
        status: data.basic.status,
        language: data.basic.language,
        timezone: data.basic.timezone,
      };
      
      const response = await apiClient.put(`/master/tenants/${tenantId}`, updatePayload);
      
      if (response.success) {
        toast({
          title: 'Tenant aggiornato',
          description: `${data.basic.name} è stato aggiornato con successo`,
        });
        setShowEditWizard(false);
        void loadTenantDetail();
      } else {
        toast({
          title: 'Errore',
          description: respErrMsg(response.error, 'Aggiornamento fallito'),
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({
        title: 'Errore',
        description: __err.message || 'Aggiornamento fallito',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!tenant || !tenantId) return;
    
    const newStatus = tenant.status === 'active' ? 'suspended' : 'active';
    const actionLabel = newStatus === 'suspended' ? 'sospendere' : 'riattivare';
    
    if (!confirm(`Sei sicuro di voler ${actionLabel} il tenant ${tenant.name}?`)) {
      return;
    }
    
    try {
      setTogglingStatus(true);
      
      const response = await apiClient.put(`/master/tenants/${tenantId}`, { status: newStatus });
      
      if (response.success) {
        toast({
          title: newStatus === 'suspended' ? 'Tenant sospeso' : 'Tenant riattivato',
          description: `${tenant.name} è stato ${newStatus === 'suspended' ? 'sospeso' : 'riattivato'} con successo`,
        });
        void loadTenantDetail();
      } else {
        toast({
          title: 'Errore',
          description: respErrMsg(response.error, 'Operazione fallita'),
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({
        title: 'Errore',
        description: __err.message || 'Operazione fallita',
        variant: 'destructive',
      });
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleResetPassword = async (userId: string, userEmail: string) => {
    if (!confirm(`Reset password for ${userEmail}? A temporary password will be generated.`)) {
      return;
    }
    
    try {
      setResettingPassword(userId);
      const response = await apiClient.post<{ temporaryPassword?: string; password?: string }>(`/master/users/${userId}/reset-password`, {});
      
      if (response.success) {
        const tempPassword = (response.data)?.temporaryPassword || (response.data)?.password;
        if (tempPassword) {
          await navigator.clipboard.writeText(tempPassword);
          toast({
            title: 'Password Reset',
            description: 'New password copied to clipboard',
          });
        } else {
          toast({
            title: 'Password Reset',
            description: 'Password reset email sent successfully',
          });
        }
      } else {
        toast({
          title: 'Error',
          description: respErrMsg(response.error, 'Failed to reset password'),
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({
        title: 'Error',
        description: __err.message || 'Failed to reset password',
        variant: 'destructive',
      });
    } finally {
      setResettingPassword(null);
    }
  };

  const handleReset2FA = async (userId: string, userEmail: string) => {
    if (!confirm(`Reset 2FA per ${userEmail}? L'utente dovrà riconfigurare il 2FA al prossimo accesso.`)) {
      return;
    }

    try {
      setResetting2FA(userId);
      const response = await apiClient.post<{ message?: string }>(`/master/users/${userId}/reset-2fa`, {});

      if (response.success) {
        toast({
          title: '2FA Reset',
          description: (response.data)?.message || '2FA resettato con successo',
        });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, totp_enabled: false } : u));
      } else {
        toast({
          title: 'Errore',
          description: respErrMsg(response.error, 'Errore nel reset del 2FA'),
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({
        title: 'Errore',
        description: __err.message || 'Errore nel reset del 2FA',
        variant: 'destructive',
      });
    } finally {
      setResetting2FA(null);
    }
  };

  const handleDeleteTenant = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast({
        title: 'Conferma richiesta',
        description: 'Scrivi DELETE per confermare',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setDeleting(true);
      const response = await apiClient.delete(`/master/tenants/${tenantId}`);
      
      if (response.success) {
        toast({
          title: 'Tenant eliminato',
          description: `${tenant?.name} è stato eliminato definitivamente`,
        });
        navigate('/tenants');
      } else {
        toast({
          title: 'Errore',
          description: respErrMsg(response.error, 'Eliminazione fallita'),
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({
        title: 'Errore',
        description: __err.message || 'Eliminazione fallita',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmText('');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Suspended</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const toggleCategoryCollapsed = (categoryKey: string) => {
    setCollapsedCategories(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
  };

  const toggleParentExpanded = (moduleKey: string) => {
    setExpandedParents(prev => ({ ...prev, [moduleKey]: !prev[moduleKey] }));
  };

  const matchesSearch = (mod: TenantModule, search: string): boolean => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (mod.name.toLowerCase().includes(q) || mod.description.toLowerCase().includes(q) || mod.key.toLowerCase().includes(q)) {
      return true;
    }
    const childKeys = getChildModules(mod.key).map(c => c.key);
    if (childKeys.length > 0) {
      const childModules = modules.filter(m => (childKeys as string[]).includes(m.key));
      return childModules.some(c => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.key.toLowerCase().includes(q));
    }
    return false;
  };

  const renderModuleToggle = (mod: TenantModule, indeterminate = false) => (
    <button
      onClick={() => toggleModule(mod.key, !mod.enabled)}
      disabled={togglingModule === mod.key}
      title={
        indeterminate
          ? 'Stato indeterminato: alcuni sotto-moduli sono attivi'
          : mod.enabled ? 'Disattiva modulo' : 'Attiva modulo'
      }
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
        indeterminate
          ? 'bg-amber-500'
          : mod.enabled ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
      } ${togglingModule === mod.key ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {togglingModule === mod.key ? (
        <Loader2 className="h-4 w-4 animate-spin text-white absolute left-1/2 -translate-x-1/2" />
      ) : indeterminate ? (
        <span className="absolute left-1/2 -translate-x-1/2 h-0.5 w-4 bg-white rounded" />
      ) : (
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            mod.enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      )}
    </button>
  );

  const renderModuleCard = (mod: TenantModule) => {
    const childKeys = getChildModules(mod.key).map(c => c.key);
    const childModules = modules.filter(m => (childKeys as string[]).includes(m.key));
    const hasChildren = childModules.length > 0;
    const enabledChildCount = childModules.filter(c => c.enabled).length;
    const totalChildren = childModules.length;
    const allChildrenEnabled = hasChildren && enabledChildCount === totalChildren;
    const someChildrenEnabled = hasChildren && enabledChildCount > 0 && enabledChildCount < totalChildren;
    const isExpanded = hasChildren ? (expandedParents[mod.key] ?? (mod.enabled || someChildrenEnabled)) : false;

    const parentToggleState = hasChildren
      ? (allChildrenEnabled && mod.enabled ? 'on' : someChildrenEnabled || mod.enabled ? 'indeterminate' : 'off')
      : (mod.enabled ? 'on' : 'off');

    return (
      <div
        key={mod.key}
        className={`rounded-lg border transition-all ${
          mod.enabled
            ? 'border-indigo-200 bg-indigo-50/50 dark:border-indigo-500/30 dark:bg-indigo-900/20'
            : 'border-border bg-muted/50 dark:border-white/10 dark:bg-white/5'
        }`}
      >
        <div className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              {hasChildren ? (
                <button
                  onClick={() => toggleParentExpanded(mod.key)}
                  className="mt-0.5 p-0.5 rounded hover:bg-white/60 dark:hover:bg-white/10 text-muted-foreground"
                  title={isExpanded ? 'Comprimi sotto-moduli' : 'Espandi sotto-moduli'}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              ) : (
                <span className="w-5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-foreground">{mod.name}</h4>
                  {hasChildren && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] uppercase tracking-wide ${
                        parentToggleState === 'indeterminate'
                          ? 'border-amber-400 text-amber-700 dark:text-amber-300'
                          : ''
                      }`}
                    >
                      {enabledChildCount}/{totalChildren} sotto-moduli
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70 mt-0.5">{mod.description}</p>
                {mod.enabled && mod.enabled_at && (
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Attivato: {new Date(mod.enabled_at).toLocaleDateString('it-IT')}
                  </p>
                )}
                {!mod.enabled && mod.disabled_at && (
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Disattivato: {new Date(mod.disabled_at).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              {renderModuleToggle(mod, parentToggleState === 'indeterminate')}
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="border-t border-border dark:border-white/10 px-4 py-3 space-y-2 bg-white/40 dark:bg-black/10 rounded-b-lg">
            {!mod.enabled && (
              <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded px-2 py-1.5">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>Modulo padre disattivato: i sotto-moduli non saranno accessibili anche se attivi.</span>
              </div>
            )}
            {childModules.map(child => (
              <div
                key={child.key}
                className={`flex items-center justify-between gap-3 pl-6 py-1.5 ${mod.enabled ? '' : 'opacity-60'}`}
              >
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${child.enabled ? 'bg-indigo-500' : 'bg-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground dark:text-foreground">{child.name}</span>
                      <code className="text-[10px] text-muted-foreground/70 font-mono">{child.key}</code>
                    </div>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">{child.description}</p>
                  </div>
                </div>
                <div className="flex-shrink-0">{renderModuleToggle(child)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderModulesByCategory = () => {
    const childKeySet = new Set<string>();
    for (const m of modules) {
      for (const c of getChildModules(m.key)) childKeySet.add(c.key);
    }
    const topLevel = modules.filter(m => !childKeySet.has(m.key));
    const filtered = topLevel.filter(m => matchesSearch(m, moduleSearch));

    const knownCategoryKeys = new Set(MODULE_CATEGORIES.map(c => c.key));
    const grouped = new Map<string, TenantModule[]>();
    for (const mod of filtered) {
      const cat = mod.category && (knownCategoryKeys as Set<string>).has(mod.category) ? mod.category : 'other';
      if (!grouped.has(cat)) grouped.set(cat, []);
      grouped.get(cat)?.push(mod);
    }

    const orderedCategories = [
      ...MODULE_CATEGORIES,
      { key: 'other' as const, label: 'Altri moduli', order: 999 },
    ].filter(c => grouped.has(c.key));

    if (orderedCategories.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          Nessun modulo corrisponde alla ricerca "{moduleSearch}".
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
          <input
            type="text"
            value={moduleSearch}
            onChange={(e) => setModuleSearch(e.target.value)}
            placeholder="Cerca moduli per nome, descrizione o chiave..."
            className="w-full pl-9 pr-9 py-2 rounded-lg bg-white/70 dark:bg-white/5 border border-border dark:border-white/10 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
          />
          {moduleSearch && (
            <button
              onClick={() => setModuleSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted dark:hover:bg-white/10"
              title="Cancella ricerca"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>

        {orderedCategories.map(cat => {
          const items = [...(grouped.get(cat.key) || [])].sort(
            (a, b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name)
          );
          const collapsed = collapsedCategories[cat.key] ?? false;
          const enabledCount = items.filter(m => m.enabled).length;

          return (
            <div key={cat.key} className="border border-border dark:border-white/10 rounded-xl overflow-hidden bg-white/40 dark:bg-white/[0.02]">
              <button
                onClick={() => toggleCategoryCollapsed(cat.key)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/70 dark:bg-white/[0.04] hover:bg-muted/60 dark:hover:bg-white/[0.07] transition-colors"
              >
                <div className="flex items-center gap-2">
                  {collapsed ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="font-semibold text-foreground">{cat.label}</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {enabledCount}/{items.length} attivi
                </Badge>
              </button>
              {!collapsed && (
                <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {items.map(renderModuleCard)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Skeleton className="h-10 w-64 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error || 'Tenant not found'}</p>
              <button
                onClick={() => navigate('/tenants')}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:border-cyan-400/50 transition-all duration-300 shadow-lg"
              >
                <ArrowLeft className="h-4 w-4 text-cyan-400 group-hover:-translate-x-1 transition-transform duration-300" />
                <span className="text-sm font-medium text-white">Torna ai Tenants</span>
              </button>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/tenants')}
            title="Torna alla lista tenant"
            className="group p-2 rounded-lg bg-white/5 hover:bg-white/15 border border-white/10 hover:border-cyan-400/40 transition-all duration-300"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground/70 group-hover:text-cyan-400 group-hover:-translate-x-0.5 transition-all duration-300" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{tenant.name}</h1>
            <p className="text-sm text-muted-foreground">Slug: {tenant.slug}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusBadge(tenant.status)}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <GlassCard variant="elevated" className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Users</p>
              <p className="text-2xl font-bold text-foreground">{tenant.user_count}</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard variant="elevated" className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Phone className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Extensions</p>
              <p className="text-2xl font-bold text-foreground">{tenant.extension_count}</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard variant="elevated" className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <PhoneCall className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">SIP Trunks</p>
              <p className="text-2xl font-bold text-foreground">{tenant.trunk_count}</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard variant="elevated" className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Store className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Stores</p>
              <p className="text-2xl font-bold text-foreground">{tenant.store_count}</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard variant="elevated" className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Activity className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Calls (24h)</p>
              <p className="text-2xl font-bold text-foreground">{tenant.call_count_24h}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <GlassCard variant="elevated" className="lg:col-span-2 p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Tenant Information</h3>
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Tenant ID</dt>
              <dd 
                className="mt-1 text-sm text-foreground font-mono cursor-help"
                title={tenant.id}
              >
                {tenant.id.slice(0, 8)}...
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="mt-1">{getStatusBadge(tenant.status)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="mt-1 text-sm text-foreground">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground/70" />
                  <span>{new Date(tenant.created_at).toLocaleDateString()}</span>
                </div>
              </dd>
            </div>
            {tenant.domain && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Domain</dt>
                <dd className="mt-1 text-sm text-foreground">{tenant.domain}</dd>
              </div>
            )}
            {tenant.sip_domain && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">SIP Domain</dt>
                <dd className="mt-1 text-sm text-foreground font-mono">{tenant.sip_domain}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Avg Call Duration</dt>
              <dd className="mt-1 text-sm text-foreground">{Math.round(tenant.avg_call_duration)}s</dd>
            </div>
          </dl>
          
          <div className="mt-6 pt-4 border-t border-border dark:border-white/10">
            <h4 className="text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-3">Call Statistics</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/50 dark:bg-white/5 rounded-lg">
                <p className="text-2xl font-bold text-foreground">{tenant.call_count_7d}</p>
                <p className="text-xs text-muted-foreground">7 Days</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{tenant.answered_calls}</p>
                <p className="text-xs text-muted-foreground">Answered</p>
              </div>
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{tenant.inbound_calls}</p>
                <p className="text-xs text-muted-foreground">Inbound</p>
              </div>
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{tenant.outbound_calls}</p>
                <p className="text-xs text-muted-foreground">Outbound</p>
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard variant="elevated" className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Actions</h3>
          <div className="space-y-2">
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => setShowCompanyInfo(true)}
            >
              <Building className="h-4 w-4 mr-2" />
              View Ragione Sociale
            </Button>
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => setShowChannelAnalytics(true)}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Channel Analytics
            </Button>
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => setShowLimitsModal(true)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Limiti tenant
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={handleEditTenant}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Tenant
            </Button>
            {tenant.status === 'active' ? (
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={handleToggleStatus}
                disabled={togglingStatus}
              >
                <Ban className="h-4 w-4 mr-2" />
                {togglingStatus ? 'Sospensione...' : 'Sospendi Tenant'}
              </Button>
            ) : (
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={handleToggleStatus}
                disabled={togglingStatus}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {togglingStatus ? 'Riattivazione...' : 'Riattiva Tenant'}
              </Button>
            )}
            <Button 
              className="w-full justify-start" 
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Tenant
            </Button>
          </div>
        </GlassCard>
      </div>

      <GlassCard variant="elevated" className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Details</h3>
          <Tabs defaultValue="users" className="w-full">
            <TabsList>
              <TabsTrigger value="modules"><Package className="h-4 w-4 mr-1.5" />Moduli</TabsTrigger>
              <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
              <TabsTrigger value="extensions">Extensions ({extensions.length})</TabsTrigger>
              <TabsTrigger value="trunks">SIP Trunks ({trunks.length})</TabsTrigger>
              <TabsTrigger value="stores">Stores ({stores.length})</TabsTrigger>
              <TabsTrigger value="supervisor-compliance">Supervisor</TabsTrigger>
              <TabsTrigger value="visibility" onClick={() => { if (!visibilityLoaded) void loadVisibility(); }}>
                <Eye className="h-4 w-4 mr-1.5" />Visibilità cataloghi
              </TabsTrigger>
              <TabsTrigger value="system-emails"><Mail className="h-4 w-4 mr-1.5" />Email di sistema</TabsTrigger>
            </TabsList>

            <TabsContent value="modules" className="mt-4">
              {modulesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                  <span className="ml-2 text-muted-foreground">Caricamento moduli...</span>
                </div>
              ) : modules.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  Nessun modulo configurato per questo tenant.
                </div>
              ) : (
                renderModulesByCategory()
              )}
            </TabsContent>

            <TabsContent value="users" className="mt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border dark:border-white/10">
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Name</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Email</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Role</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Created</th>
                      <th className="text-right py-3 px-4 text-foreground dark:text-muted-foreground/60">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-muted-foreground">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id} className="border-b border-border dark:border-white/10 hover:bg-muted/50 dark:hover:bg-white/5">
                          <td className="py-3 px-4 text-foreground">
                            {user.first_name} {user.last_name}
                          </td>
                          <td className="py-3 px-4 text-foreground">{user.email}</td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary">{user.role}</Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-right flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPassword(user.id, user.email)}
                              disabled={resettingPassword === user.id}
                              title="Reset Password"
                            >
                              {resettingPassword === user.id ? (
                                <RotateCcw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Key className="h-4 w-4" />
                              )}
                            </Button>
                            {user.totp_enabled && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReset2FA(user.id, user.email)}
                                disabled={resetting2FA === user.id}
                                title="Reset 2FA"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              >
                                {resetting2FA === user.id ? (
                                  <RotateCcw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ShieldOff className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="extensions" className="mt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border dark:border-white/10">
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Extension</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Display Name</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extensions.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-8 text-muted-foreground">
                          No extensions found
                        </td>
                      </tr>
                    ) : (
                      extensions.map((ext) => (
                        <tr key={ext.id} className="border-b border-border dark:border-white/10 hover:bg-muted/50 dark:hover:bg-white/5">
                          <td className="py-3 px-4 font-mono text-foreground">{ext.extension_number}</td>
                          <td className="py-3 px-4 text-foreground">{ext.display_name}</td>
                          <td className="py-3 px-4">
                            <Badge variant={ext.status === 'active' ? 'default' : 'secondary'}>
                              {ext.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="trunks" className="mt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border dark:border-white/10">
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Name</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Host</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Port</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trunks.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-muted-foreground">
                          No SIP trunks configured
                        </td>
                      </tr>
                    ) : (
                      trunks.map((trunk) => (
                        <tr key={trunk.id} className="border-b border-border dark:border-white/10 hover:bg-muted/50 dark:hover:bg-white/5">
                          <td className="py-3 px-4 text-foreground">{trunk.name}</td>
                          <td className="py-3 px-4 font-mono text-foreground">{trunk.host}</td>
                          <td className="py-3 px-4 text-foreground">{trunk.port}</td>
                          <td className="py-3 px-4">
                            <Badge variant={trunk.status === 'active' ? 'default' : 'secondary'}>
                              {trunk.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="stores" className="mt-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border dark:border-white/10">
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Name</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Store ID</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Status</th>
                      <th className="text-left py-3 px-4 text-foreground dark:text-muted-foreground/60">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-muted-foreground">
                          No stores configured
                        </td>
                      </tr>
                    ) : (
                      stores.map((store) => (
                        <tr key={store.id} className="border-b border-border dark:border-white/10 hover:bg-muted/50 dark:hover:bg-white/5">
                          <td className="py-3 px-4 text-foreground">{store.name}</td>
                          <td className="py-3 px-4 font-mono text-foreground">{store.store_id}</td>
                          <td className="py-3 px-4">
                            <Badge variant={store.status === 'active' ? 'default' : 'secondary'}>
                              {store.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {store.created_at ? new Date(store.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="supervisor-compliance" className="mt-4">
              <SupervisorComplianceSection tenantId={tenantId} />
            </TabsContent>

            <TabsContent value="visibility" className="mt-4">
              <div className="space-y-6">
                <div className="text-sm text-muted-foreground">
                  Controlla quali brand retail e aree commerciali sono visibili a questo tenant nella selezione sede. Disattivare un elemento lo nasconde dai dropdown del tenant senza impatti sui dati esistenti.
                </div>

                {visibilityLoading && !visibilityLoaded ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                    <span className="ml-2 text-muted-foreground">Caricamento visibilità...</span>
                  </div>
                ) : (
                  <>
                    <GlassCard className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-base font-semibold text-foreground">Brand retail</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {visibilityBrands.filter(b => b.visible).length} di {visibilityBrands.length} visibili
                          </p>
                        </div>
                        <Button size="sm" onClick={() => void saveBrandsVisibility()} disabled={savingBrands || visibilityLoading}>
                          {savingBrands ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                          Salva
                        </Button>
                      </div>
                      {visibilityBrands.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">Nessun brand attivo nel catalogo.</div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {visibilityBrands.map(b => (
                            <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border dark:border-white/10 hover:bg-muted/30 dark:hover:bg-white/5">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-foreground truncate">{b.display_name}</div>
                                <div className="text-xs text-muted-foreground font-mono truncate">{b.code}</div>
                              </div>
                              <Switch checked={b.visible} onCheckedChange={() => toggleBrandVisibility(b.id)} />
                            </div>
                          ))}
                        </div>
                      )}
                    </GlassCard>

                    <GlassCard className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-base font-semibold text-foreground">Aree commerciali</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {visibilityAreas.filter(a => a.visible).length} di {visibilityAreas.length} visibili
                          </p>
                        </div>
                        <Button size="sm" onClick={() => void saveAreasVisibility()} disabled={savingAreas || visibilityLoading}>
                          {savingAreas ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                          Salva
                        </Button>
                      </div>
                      {visibilityAreas.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">Nessuna area commerciale attiva nel catalogo.</div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {visibilityAreas.map(a => (
                            <div key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border dark:border-white/10 hover:bg-muted/30 dark:hover:bg-white/5">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-foreground truncate">{a.display_name}</div>
                                <div className="text-xs text-muted-foreground font-mono truncate">{a.code}</div>
                              </div>
                              <Switch checked={a.visible} onCheckedChange={() => toggleAreaVisibility(a.id)} />
                            </div>
                          ))}
                        </div>
                      )}
                    </GlassCard>
                  </>
                )}
              </div>
            </TabsContent>
            <TabsContent value="system-emails" className="mt-4">
              <TenantSystemEmails tenantId={tenantId} />
            </TabsContent>
          </Tabs>
      </GlassCard>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowDeleteDialog(false);
              setDeleteConfirmText('');
            }}
          />
          <div className="relative bg-white dark:bg-slate-900 border border-red-300 dark:border-red-500/50 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Elimina Tenant</h3>
              <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">
                Stai per eliminare definitivamente <span className="text-foreground font-semibold">{tenant?.name}</span>. 
                Questa azione è irreversibile e cancellerà tutti i dati associati.
              </p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-muted-foreground dark:text-muted-foreground/70 mb-2">
                Scrivi <span className="text-red-500 dark:text-red-400 font-mono font-bold">DELETE</span> per confermare:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-4 py-3 bg-muted border border-border dark:border-slate-700 rounded-xl text-foreground placeholder-slate-400 dark:placeholder-slate-500 focus:border-red-500 focus:ring-2 focus:ring-inset focus:ring-red-500 outline-none transition-all font-mono text-center text-lg tracking-widest"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
              >
                Annulla
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteTenant}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
              >
                {deleting ? 'Eliminazione...' : 'Elimina Definitivamente'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Wizard */}
      <TenantWizard
        open={showEditWizard}
        onClose={() => setShowEditWizard(false)}
        onSubmit={handleUpdateTenant}
        editMode={true}
        initialData={editWizardData ?? undefined}
        isSubmitting={isUpdating}
      />

      {/* Channel Analytics Modal */}
      <ChannelAnalyticsModal
        open={showChannelAnalytics}
        onClose={() => setShowChannelAnalytics(false)}
        tenant={tenant ? { id: tenant.id, name: tenant.name } : null}
      />

      {/* Tenant Limits Modal (VoIP / Users / AI Agents) */}
      <TenantLimitsModal
        open={showLimitsModal}
        onClose={() => setShowLimitsModal(false)}
        tenant={tenant ? { id: tenant.id, name: tenant.name } : null}
      />

      {/* Ragione Sociale Modal */}
      {showCompanyInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCompanyInfo(false)}
          />
          <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-slate-900 p-6 border-b border-border dark:border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Building className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Ragione Sociale</h2>
                  <p className="text-sm text-muted-foreground">{tenant?.name}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCompanyInfo(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6">
              {tenantRawData?.tenant?.companies && tenantRawData.tenant.companies.length > 0 ? (
                <div className="space-y-6">
                  {tenantRawData.tenant.companies.map((company, index: number) => (
                    <div key={index} className="bg-muted/50 dark:bg-white/5 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-foreground mb-4">
                        {company.legal_name || 'Azienda senza nome'}
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Forma Giuridica</p>
                          <p className="text-sm text-foreground">{company.legal_form || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">P.IVA</p>
                          <p className="text-sm text-foreground font-mono">{company.vat_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Codice Fiscale</p>
                          <p className="text-sm text-foreground font-mono">{company.tax_code || company.fiscal_code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">PEC</p>
                          <p className="text-sm text-foreground">{company.pec || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Indirizzo</p>
                          <p className="text-sm text-foreground">
                            {company.address ? `${company.address}, ${company.postal_code || ''} ${company.city || ''} (${company.province || ''})` : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Telefono</p>
                          <p className="text-sm text-foreground">{company.phone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Paese</p>
                          <p className="text-sm text-foreground">{company.country || 'Italia'}</p>
                        </div>
                        {company.description && (
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Note</p>
                            <p className="text-sm text-foreground">{company.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building className="h-12 w-12 text-muted-foreground/60 dark:text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground dark:text-muted-foreground/70">Nessuna ragione sociale associata a questo tenant</p>
                  <p className="text-sm text-muted-foreground/70 dark:text-muted-foreground mt-1">Puoi aggiungere le informazioni aziendali modificando il tenant</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
